import {
  useAddressForm,
  useGetAddressAlertMessage,
} from '../hooks/address-form.hook';
import { useBrainTreeHook } from '../hooks/braintreeHook';
import { useCartHook } from '../hooks/cartHook';
import { useIdleHook } from '../hooks/idle.hook';
import { Cornerstone } from '@rpi/api';
import dropin, { Dropin } from 'braintree-web-drop-in';
import React from 'react';
import { useLocation } from 'react-router-dom';

export type BillingAddressState = 'SELECT' | 'ADD' | 'EDIT';
export type CheckoutState = 'SHIPPING_ADDRESS' | 'SHIPPING_TIER' | 'PAYMENT';
export type CouponError = ReturnType<
  typeof Cornerstone.useUpdateCheckout | typeof Cornerstone.useCheckoutPreview
>['error'];

type CheckoutContext = {
  checkoutState: CheckoutState;
  checkoutData: Cornerstone.CheckoutSummary;
  checkoutIsLoading: boolean;
  checkoutIsError: boolean;
  checkoutCreated: boolean;
  selectedShippingAddress?: Cornerstone.ValueAddress;
  selectedBillingAddress?: Cornerstone.ValueAddress;
  addBillingAddressForm: ReturnType<typeof useAddressForm>;
  editBillingAddressForm: ReturnType<typeof useAddressForm>;
  orderData?: Cornerstone.OrderSummary | null;
  orderError?: Error;

  errors: {
    shippingAddress?: Error;
    shippingTier?: Error;
    coupon?: CouponError;
    order?: Error;
  };

  loading: {
    shippingAddress?: boolean;
    shippingTier?: boolean;
    coupon?: boolean;
    order?: boolean;
  };

  setCheckoutState: (state: CheckoutState) => void;
  applyCoupon: (coupon: string) => void;
  setShippingAddress: (address: Cornerstone.ValueAddress) => void;
  setShippingTier: (option: keyof typeof Cornerstone.ShippingTier) => void;
  setSelectedBillingAddress: (address?: Cornerstone.ValueAddress) => void;
  setBillingAddressState: (state: BillingAddressState) => void;
  onOrder: () => void;
  braintreeError?: Error;
  isPaymentRequestable: boolean;
  setBraintreeError: (error?: Error) => void;
  onInstanceChanged: (instance: Dropin | undefined) => void;
  billingAddressRequired: boolean;
};

const useCheckoutTimeout = () => {
  const checkoutTimeout = React.useCallback(() => {
    window.location.reload();
  }, []);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const fifteenMinutes = 15 * 60 * 1000;
  const resetTime = Number(queryParams.get('resetTime') ?? fifteenMinutes);

  useIdleHook(Math.min(fifteenMinutes, resetTime), checkoutTimeout);
};

const useDiscount = (
  checkoutData: Cornerstone.CheckoutSummary,
  setCheckoutData: (checkoutData: Cornerstone.CheckoutSummary) => void,
) => {
  const [selectedDiscount, setSelectedDiscount] = React.useState<
    string | undefined
  >(undefined);

  const checkoutId = checkoutData.checkoutId;
  const shippingTier =
    checkoutData.shippingTier ?? Cornerstone.ShippingTier.ECONOMY;

  const {
    mutate: cartCouponMutate,
    reset: cartCouponReset,
    error: cartCouponError,
    isLoading: cartCouponLoading,
  } = Cornerstone.useCheckoutPreview({
    mutation: {
      onMutate: () => {
        if (checkoutData.discountsUsed.length === 0) {
          return undefined;
        }
        const { applicationMode, discountCode } = checkoutData.discountsUsed[0];

        const prevDiscount =
          applicationMode === Cornerstone.DiscountApplicationMode.COUPON
            ? discountCode
            : '';

        return { prevDiscount };
      },
      onSuccess: (data) => {
        setCheckoutData(data.data);
      },
      onError: (
        error: any,
        _variables,
        context?: { prevDiscount: string | undefined },
      ) => {
        setSelectedDiscount(context?.prevDiscount);

        console.log({
          title: 'Failed to load cart data after applying coupon',
          description: error?.response?.data?.message || error.message,
          type: 'error',
        });
      },
    },
  });

  const {
    mutate: checkoutCouponMutate,
    isLoading: checkoutCouponLoading,
    error: checkoutCouponError,
  } = Cornerstone.useUpdateCheckout({
    mutation: {
      onMutate: () => {
        const prevDiscount = checkoutData.discountsUsed[0]?.discountCode ?? '';
        return { prevDiscount };
      },
      onSuccess: (data) => {
        setCheckoutData(data.data);
        cartCouponReset();
      },
      onError: (error: any, _variables: any, context: any) => {
        setSelectedDiscount(context.prevDiscount);
        console.log({
          title: 'Failed to apply coupon',
          description: error?.response?.data?.message || error.message,
          type: 'error',
        });
      },
    },
  });

  const applyCoupon = React.useCallback(
    (discountCode: string) => {
      setSelectedDiscount(discountCode);

      if (checkoutId) {
        checkoutCouponMutate({
          checkoutId,
          data: {
            shippingTier,
            discountCode,
          },
        });
      } else {
        cartCouponMutate({
          data: {
            discountCode,
          },
        });
      }
    },
    [checkoutId, shippingTier, cartCouponMutate, checkoutCouponMutate],
  );

  return {
    selectedDiscount,
    setSelectedDiscount,
    applyCoupon,
    couponLoading: cartCouponLoading || checkoutCouponLoading,
    couponError: cartCouponError || checkoutCouponError,
  };
};

const useShippingAddress = (
  selectedDiscount: string | undefined,
  setCheckoutData: (checkoutData: Cornerstone.CheckoutSummary) => void,
  setCheckoutState: (state: CheckoutState) => void,
) => {
  const [selectedShippingAddress, setSelectedShippingAddress] = React.useState<
    Cornerstone.ValueAddress | undefined
  >(undefined);

  const {
    isLoading: createLoading,
    isSuccess: createSuccess,
    error: createError,
    mutate: createCheckout,
  } = Cornerstone.useCreateCheckoutV2({
    mutation: {
      onError: (error: any) => {
        console.log({
          title: 'Failed to update account',
          description: error?.response?.data?.message || error.message,
          type: 'error',
        });
      },
    },
  });

  const setShippingAddress = React.useCallback(
    (address: Cornerstone.ValueAddress) => {
      createCheckout(
        {
          data: {
            shippingAddress: address,
            discountCode: selectedDiscount,
          },
        },
        {
          onSuccess: (data) => {
            setCheckoutData(data.data);
            setSelectedShippingAddress(address);
            setCheckoutState('SHIPPING_TIER');
          },
        },
      );
    },
    [createCheckout, selectedDiscount, setCheckoutData, setCheckoutState],
  );

  return {
    shippingAddressLoading: createLoading,
    shippingAddressError: createError,
    shippingAddressSuccess: createSuccess,
    selectedShippingAddress,
    setShippingAddress,
  };
};

const useBillingAddress = () => {
  const [selectedBillingAddress, setSelectedBillingAddress] = React.useState<
    Cornerstone.ValueAddress | undefined
  >(undefined);
  const [billingAddressState, setBillingAddressState] =
    React.useState<BillingAddressState>('SELECT');

  const getAddressAlertMessage = useGetAddressAlertMessage();

  const addBillingAddressForm = useAddressForm();
  const editBillingAddressForm = useAddressForm();

  const { mutate: addAddress } = Cornerstone.useAddAddress({
    mutation: {
      onError: (error: any) => {
        console.error({
          title: 'Failed to add address',
          description: error?.response?.data?.message || error.message,
          type: 'error',
        });

        const errorMessage = getAddressAlertMessage(error);

        if (!errorMessage) return;

        addBillingAddressForm.form.setError('root.serverError', {
          message: errorMessage,
        });
      },
    },
  });

  const { mutate: updateAddress } = Cornerstone.useUpdateAddress({
    mutation: {
      onError: (error: any) => {
        console.error({
          title: 'Failed to update address',
          description: error?.response?.data?.message || error.message,
          type: 'error',
        });

        const errorMessage = getAddressAlertMessage(error);

        if (!errorMessage) return;

        editBillingAddressForm.form.setError('root.serverError', {
          message: errorMessage,
        });
      },
    },
  });

  const addBillingAddressHandleSubmit = addBillingAddressForm.form.handleSubmit;
  const editBillingAddressHandleSubmit =
    editBillingAddressForm.form.handleSubmit;

  const canSubmitBillingAddress: boolean =
    billingAddressState === 'ADD' ||
    (billingAddressState === 'EDIT' &&
      selectedBillingAddress?.id !== undefined) ||
    (billingAddressState === 'SELECT' && selectedBillingAddress !== undefined);

  const submitBillingAddressCallback = React.useCallback(
    (onSuccess: (address: Cornerstone.ValueAddress) => void) => {
      const billingAddressId = selectedBillingAddress?.id;
      if (billingAddressState === 'ADD') {
        addBillingAddressHandleSubmit((formData) =>
          addAddress(
            { data: formData },
            { onSuccess: (response) => onSuccess(response.data) },
          ),
        )();
      } else if (billingAddressState === 'EDIT' && billingAddressId) {
        editBillingAddressHandleSubmit((formData) =>
          updateAddress(
            { addressId: billingAddressId, data: formData },
            { onSuccess: (response) => onSuccess(response.data) },
          ),
        )();
      } else if (billingAddressState === 'SELECT' && selectedBillingAddress) {
        onSuccess(selectedBillingAddress);
      } else {
        throw new Error('billing address is invalid');
      }
    },
    [
      billingAddressState,
      selectedBillingAddress,
      addAddress,
      updateAddress,
      addBillingAddressHandleSubmit,
      editBillingAddressHandleSubmit,
    ],
  );

  return {
    selectedBillingAddress,
    addBillingAddressForm,
    editBillingAddressForm,
    canSubmitBillingAddress,
    setSelectedBillingAddress,
    submitBillingAddress: canSubmitBillingAddress
      ? submitBillingAddressCallback
      : undefined,
    setBillingAddressState,
  };
};

const useShippingTier = (
  checkoutData: Cornerstone.CheckoutSummary,
  setCheckoutData: (checkoutData: Cornerstone.CheckoutSummary) => void,
  selectedDiscount: string | undefined,
  setSelectedDiscount: (discountCode?: string) => void,
) => {
  const checkoutId = checkoutData.checkoutId;

  const {
    mutate: updateCheckout,
    isLoading: updateLoading,
    error: updateError,
  } = Cornerstone.useUpdateCheckout({
    mutation: {
      onMutate: (variables) => {
        const fallbackCheckoutData = checkoutData;

        if (checkoutData) {
          // immediately update the tier for responsiveness
          setCheckoutData({
            ...checkoutData,
            shippingTier: variables.data.shippingTier,
          });
        }

        return { fallbackCheckoutData };
      },
      onSuccess: (data) => {
        setCheckoutData(data.data);
      },
      onError: (
        error: any,
        _variables,
        context?: { fallbackCheckoutData: Cornerstone.CheckoutSummary },
      ) => {
        if (context?.fallbackCheckoutData) {
          setCheckoutData(context.fallbackCheckoutData);
          setSelectedDiscount(
            context.fallbackCheckoutData.discountsUsed[0].discountCode,
          );
        }

        console.log({
          title: 'Failed to update account',
          description: error?.response?.data?.message || error.message,
          type: 'error',
        });
      },
    },
  });

  const setShippingTier = React.useCallback(
    (option: keyof typeof Cornerstone.ShippingTier) => {
      if (checkoutId) {
        updateCheckout({
          checkoutId,
          data: {
            shippingTier: option,
            discountCode: selectedDiscount,
          },
        });
      }
    },
    [checkoutId, updateCheckout, selectedDiscount],
  );

  return {
    setShippingTier,
    shippingTierError: updateError,
    shippingTierLoading: updateLoading,
  };
};

const usePayment = (
  checkoutData: Cornerstone.CheckoutSummary,
  onApplyPayment: () => Promise<dropin.PaymentMethodPayload | undefined>,
  submitBillingAddress?: (
    onSuccess: (address: Cornerstone.ValueAddress) => void,
  ) => void,
) => {
  const { clearCart } = useCartHook();
  const {
    data: orderData,
    isLoading: orderLoading,
    error: orderError,
    mutate: placeOrder,
  } = Cornerstone.usePlaceOrder({
    mutation: {
      onSuccess: clearCart,
      onError: (error: any) => {
        console.log({
          title: 'Failed to place',
          description: error?.response?.data?.message || error.message,
          type: 'error',
        });
      },
    },
  });

  const handleApplyPayment = React.useCallback(
    async (address: Cornerstone.ValueAddress | undefined) => {
      const paymentMethod = await onApplyPayment();
      if (!paymentMethod) {
        throw new Error('Payment method is undefined');
      }

      if (!checkoutData?.checkoutId) {
        throw new Error('Missing checkout ID');
      }

      if (!checkoutData.shippingTier) {
        throw new Error('Missing shipping tier');
      }

      const { nonce, deviceData } = paymentMethod;

      if (!nonce || !deviceData) {
        console.error('Missing required payment method data');
        throw new Error('Missing required payment method data');
      }

      const data = {
        billingAddress: address,
        paymentMethodNonce: nonce,
        deviceData,
        shippingTier: checkoutData.shippingTier,
      };

      placeOrder({ id: checkoutData.checkoutId, data });
    },
    [
      checkoutData.checkoutId,
      checkoutData.shippingTier,
      placeOrder,
      onApplyPayment,
    ],
  );

  const onOrder = React.useCallback(async () => {
    // submitBillingAddress is a function that will handle
    // the billing address form submission or getting a selected
    // address and all required side effects. It will then
    // execute the provided callback which accepts a valid
    // ValueAddress

    if (submitBillingAddress) {
      submitBillingAddress((address) => {
        handleApplyPayment(address);
      });
    } else {
      handleApplyPayment(undefined);
    }
  }, [submitBillingAddress, handleApplyPayment]);

  return {
    onOrder,
    orderData: orderData?.data ?? null,
    orderLoading,
    orderError,
  };
};

export const CheckoutContext = React.createContext<CheckoutContext | null>(
  null,
);

export const useCheckoutContext = () => {
  const checkoutContext = React.useContext(CheckoutContext);

  if (!checkoutContext) {
    throw new Error(
      'useCheckoutContext has to be used within <CheckoutContext.Provider>',
    );
  }

  return checkoutContext;
};

type CheckoutProviderProps = React.PropsWithChildren<{
  initialCheckoutData: Cornerstone.CheckoutSummary;
}>;

export const CheckoutProvider: React.FC<CheckoutProviderProps> = ({
  children,
  initialCheckoutData,
}) => {
  const [checkoutState, setCheckoutState] =
    React.useState<CheckoutState>('SHIPPING_ADDRESS');
  const [checkoutData, setCheckoutData] =
    React.useState<Cornerstone.CheckoutSummary>(initialCheckoutData);

  // Will reload the page when a timeout happens
  useCheckoutTimeout();

  const {
    selectedDiscount,
    setSelectedDiscount,
    applyCoupon,
    couponLoading,
    couponError,
  } = useDiscount(checkoutData, setCheckoutData);

  // Manages the shipping addresses
  const {
    setShippingAddress,
    shippingAddressLoading,
    shippingAddressError,
    shippingAddressSuccess,
    selectedShippingAddress,
  } = useShippingAddress(selectedDiscount, setCheckoutData, setCheckoutState);

  const {
    braintreeError,
    isPaymentRequestable,
    onInstanceChanged,
    setBraintreeError,
    onApplyPayment,
    billingAddressRequired,
  } = useBrainTreeHook(checkoutData.total.amount, checkoutData.currency);

  const {
    selectedBillingAddress,
    addBillingAddressForm,
    editBillingAddressForm,
    canSubmitBillingAddress,
    setSelectedBillingAddress,
    submitBillingAddress,
    setBillingAddressState,
  } = useBillingAddress();

  const { setShippingTier, shippingTierError, shippingTierLoading } =
    useShippingTier(
      checkoutData,
      setCheckoutData,
      selectedDiscount,
      setSelectedDiscount,
    );

  const { onOrder, orderData, orderLoading, orderError } = usePayment(
    checkoutData,
    onApplyPayment,
    submitBillingAddress,
  );

  const checkoutIsLoading =
    shippingAddressLoading ||
    shippingTierLoading ||
    couponLoading ||
    orderLoading;

  // Excludes discount error since that entry is handled separately.
  // If a discount is applied, but fails, it automatically reverts the
  // checkout to its previous discount state.
  const checkoutIsError = !!shippingAddressError || !!shippingTierError;

  return (
    <CheckoutContext.Provider
      value={{
        applyCoupon,
        setShippingAddress,
        setShippingTier,
        setSelectedBillingAddress,
        setBillingAddressState,
        onOrder,
        orderData,
        orderError,
        checkoutData,
        checkoutState,
        setCheckoutState,
        errors: {
          coupon: couponError,
          shippingAddress: shippingAddressError,
          shippingTier: shippingTierError,
        },
        loading: {
          coupon: couponLoading,
          shippingAddress: shippingAddressLoading,
          shippingTier: shippingTierLoading,
        },
        checkoutCreated: shippingAddressSuccess,
        checkoutIsLoading,
        checkoutIsError,
        selectedShippingAddress,
        selectedBillingAddress,
        addBillingAddressForm,
        editBillingAddressForm,
        braintreeError,
        isPaymentRequestable:
          isPaymentRequestable &&
          (!billingAddressRequired || canSubmitBillingAddress),
        billingAddressRequired,
        onInstanceChanged,
        setBraintreeError,
      }}
    >
      {children}
    </CheckoutContext.Provider>
  );
};
